/**
 * HeroChat — the chat-home landing's purpose-built chat surface.
 *
 * Deliberately NOT the workspace ChatPanel: no session tabs, no header
 * chrome. The landing rail owns session switching (controlled `sessionId`
 * prop) and this surface is a bare message stream floating on the ambient
 * stage plus one clean composer.
 *
 * Context chip: chat-home has no editor tabs, but it DOES surface a file —
 * the one open in the side panel (recents / a link in the conversation).
 * The host passes that file down as `activeTab` and we render the shared
 * `EditorContextChip`, carrying it into `sendMessage` as turn context (same
 * plumbing the workspace panel uses). `selection` is optional and null for
 * now; the future drive-agent surface will feed a selected row through the
 * same prop pair.
 *
 * It reuses the same engine as the workspace panel — useAgentSession
 * (streaming, permissions, questions, steering, retry), MessageList,
 * attachments (paperclip + paste → the session-private CAS store, #639
 * Task 3.4), and the session
 * keybook (credential bindings) — so agent behavior is identical; only
 * the shell differs. Draft → real-session materialization mirrors
 * ChatPanel's: create on first send/attach, hand the new id up via
 * onSessionCreated, and pass the explicit session id to sendMessage so
 * the in-flight turn targets the right session while the hook re-binds.
 *
 * Composer buttons: Send (Enter) always; a mid-run non-empty send steers the
 * running turn. Stop is a SEPARATE affordance shown whenever a run is in
 * flight, so a stuck run can be halted even while steering text is typed
 * (parity with ChatPanel — see BUG B fix).
 *
 * Per-message actions (restore-versions / branch / summarize + edit & re-send)
 * are wired through MessageList → MessageBubble exactly as ChatPanel does,
 * driving RestoreVersionsModal / EditSendDialog. (Revert and its conflict
 * modal were removed in #768.) Persona is the only ChatPanel piece omitted — chat-home has
 * no persona picker, so MessageList falls back to the default persona.
 *
 * Empty state: HeroWelcome (below) replaces MessageList's persona picker —
 * personas aren't wired on this surface, and the landing's job is to teach
 * the two ways in: describe a mini app, or start from uploaded files.
 */
import i18n from '@papan/i18n'
import { useState, useRef, useCallback, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Send, StopCircle, BookKey, FileUp, AlertTriangle, History } from 'lucide-react'
import { MessageList } from './MessageList.js'
import { UploadsPausedNotice } from './UploadsPausedNotice'
import { ChatQuotaBanner } from './ChatQuotaBanner.js'
import { ComposerAttachButton } from './ComposerAttachButton.js'
import { truncateAndResend, mergeDraft } from './editResend.js'
import { createSendGate } from './send-gate.js'
import { sendMidRun } from './send-mid-run.js'
import { createDraftMaterializer } from './draft-materialize.js'
import { withoutSent, restoreListByKey, updateListItemById, setKeyedList } from './composer-state.js'
import { ModelSelector, DEFAULT_MODEL } from './ModelSelector.js'
import { AutoModeButton } from './AutoModeButton.js'
import { useConversationAutoMode } from './useConversationAutoMode.js'
import { useReasoningVariant } from './useReasoningVariant.js'
import { AttachmentArea, processFiles } from './AttachmentArea.js'
import { markAttachmentMoved } from './attachmentLogic.js'
import { useComposerAttachmentControls, unsendableAttachmentNotice, editNotSentMessage } from './composerAttachments.js'
import { EditorContextChip, type ActiveTabInfo } from './SelectionChip.js'
import { EditMessageButtons } from './EditMessageButtons.js'
import { SessionKeybookPopover } from './SessionKeybookPopover.js'
import { AddSessionCredentialDialog } from './AddSessionCredentialDialog.js'
import { CredentialPrompt } from './CredentialPrompt.js'
import { useSessionCredentials } from './useSessionCredentials.js'
import { sessionDialogEnvMapping, sessionDialogFields, hasOpenablePending } from './session-credential-dialog.js'
import { RestoreVersionsModal, describeRestored, type RestoredFile } from './RestoreVersionsModal.js'
import { useAgentSession, useAgentSessions, useAgentUsage, getClientId, describeSummarize, describeSteerRefusal, isTurnLive } from './hooks.js'
import type { Attachment } from './hooks.js'
import type { MessageAction } from './MessageActionsMenu.js'
import { useDisplayOptions } from './useDisplayOptions.js'
import { usePromptHistory } from './usePromptHistory.js'
import { request } from '@papan/api-client'
import { useToast, useTheme, getThemeClass, useWriteStatus, PDAppIcon, useKeybookAvailable } from '@papan/core-ui/ui'

/** Dialog shown when editing a message that has file changes — offers restore
 *  or keep. Ported verbatim from ChatPanel (not exported there, and ChatPanel
 *  is the reference we must not touch), so the edit-with-changes flow reaches
 *  parity on chat-home too. (Its "Revert AI changes & send" was removed with
 *  ChatPanel's, #768.) */
function EditSendDialog({ onChoice, onCancel }: {
  onChoice: (choice: 'restore' | 'keep') => void
  onCancel: () => void
}) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onCancel} />
      <div className={`relative z-10 rounded-lg shadow-xl max-w-md w-full ${themeClass} themed-bg-secondary`}>
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className="shrink-0 mt-0.5">
              <AlertTriangle size={24} className="text-amber-500" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold themed-text mb-2">File changes detected</h3>
              <p className="themed-text-secondary text-sm">
                The AI made file changes. How would you like to handle them before resending?
              </p>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-2 px-6 py-4 border-t themed-border">
          <button
            onClick={() => onChoice('restore')}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors"
          >
            <History size={14} />
            Restore versions & send
          </button>
          <button
            onClick={() => onChoice('keep')}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg themed-btn-secondary transition-colors"
          >
            Keep changes & send
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

interface Props {
  workspaceId: string
  /** Project folder. No longer consumed by the attachment pipeline (#639,
   *  Task 3.4 — uploads go to the session-private store, not `.temp/` under
   *  this folder); kept in the prop contract for callers that still pass
   *  it and any future non-attachment use. */
  workspaceFolderId?: string | null
  orgEmail?: string
  /** Controlled active session — null shows the fresh-chat state; the first
   *  send materializes a server session and reports it via onSessionCreated. */
  sessionId: string | null
  onSessionCreated?: (sessionId: string) => void
  /** Fired when a turn completes — the landing refreshes its rail lists. */
  onTurnComplete?: () => void
  onOpenFile?: (path: string, at?: { line?: number }) => void
  /** Open Settings → Billing from the error banner when a run was refused for
   *  lack of AI credit and the reader can fix it. Callback, not a route: this
   *  package has no router. Hidden when the host doesn't provide one. */
  onOpenBilling?: () => void
  /** File currently surfaced by the host (side-panel editor / drive selection).
   *  Shown as a context chip and carried into the next turn. Null → no chip. */
  activeTab?: ActiveTabInfo | null
  /** Optional selection within `activeTab` (line/cell/element/row). Null on
   *  chat-home today; the drive-agent surface will populate it. */
  selection?: import('@papan/collab/client').EditorSelection | null
  /** `activeTab` has unsaved editor edits — badges the chip and warns the
   *  agent (via the turn's activeTab context) that saved reads may be stale. */
  unsaved?: boolean
  /** Whether the caller can write to this workspace — gates paste/drop/
   *  local-attach uploads to the session-private store (#639, Task 3.4).
   *  Undefined = host didn't wire a signal (no caller of HeroChat computes
   *  one today); defaults to `true` to preserve prior behavior rather than
   *  silently blocking every attachment. The server enforces this
   *  independently via `mayWriteWorkspace`, so this is disclosure only. */
  canWrite?: boolean
  /** Take the member to the storage screen from the chat-storage banner
   *  (#758). Omitted → the banner still warns, just without the way out. */
  onManageChatStorage?: () => void
}

export function HeroChat({ workspaceId, orgEmail, sessionId, onSessionCreated, onTurnComplete, onOpenFile, onOpenBilling, activeTab, selection, unsaved, canWrite = true, onManageChatStorage }: Props) {
  const { sessions, loaded: sessionsLoaded, createSession, setSessionAutoMode, removeAlwaysGrant, fetchSession, autoModeDefault, setAutoModeDefault, reloadAutoModeDefault, refresh: refreshSessions } = useAgentSessions(workspaceId, orgEmail)
  const { options: displayOptions } = useDisplayOptions()
  // The deployment's chat-cost policy (0174). HeroChat renders the same
  // MessageList as the workspace panel, so it needs the same answer — without
  // it the badge would default to hidden here and show there, which reads as a
  // bug in one of the two surfaces rather than as a setting.
  const { costVisibility } = useAgentUsage(workspaceId)
  const history = usePromptHistory(workspaceId)

  const [message, setMessage] = useState('')
  // E7 Codex round 10: the session the composer belongs to NOW (a steer's text is restored only into its own session)
  const heroSessionRef = useRef<string | null>(sessionId)
  heroSessionRef.current = sessionId
  // #1292 I2: one chip list PER CHAT, keyed by the session the chips belong to
  // (as ChatPanel's). One list for the whole surface kept chat A's chips after a
  // switch to chat B, and B's send then carried A's handles — refused as unknown.
  const [attachmentsMap, setAttachmentsMap] = useState<Record<string, Attachment[]>>({})
  /** The session this view just created, reused until the `sessionId` prop
   *  changes — a paste right after the create resolves, before the parent
   *  re-renders with the new id, must not create a second chat. */
  const lastCreatedRef = useRef<string | null>(null)
  /** The chat whose chips the composer shows and edits now: the session on
   *  screen, or the one this view just created (its chips are added before the
   *  parent hands the new id down). Read live, never from a render's closure. */
  const heroAttachmentKey = useCallback((): string => heroSessionRef.current ?? lastCreatedRef.current ?? '', [])
  const attachments = attachmentsMap[sessionId ?? lastCreatedRef.current ?? ''] || []
  const setAttachments = useCallback((val: Attachment[] | ((prev: Attachment[]) => Attachment[])) => {
    setAttachmentsMap(prev => setKeyedList(prev, heroAttachmentKey(), val))
  }, [heroAttachmentKey])
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Context chip: an on/off toggle, not a dismiss — the chip stays visible so
  // the user can flip the file's context back on. Re-arms (on) whenever the
  // host surfaces a different file.
  const [contextActive, setContextActive] = useState(true)
  useEffect(() => { setContextActive(true) }, [activeTab?.fileId])
  // Carry the unsaved flag with the file so the turn's activeTab context can
  // warn the agent that it's only seeing the saved version (see system.ts
  // buildActiveTabContext).
  const contextTab = activeTab && contextActive ? { ...activeTab, unsaved } : undefined
  const contextSelection = contextTab ? (selection ?? undefined) : undefined

  // Model preference — same workspace-scoped persistence as ChatPanel.
  const [selectedModel, setSelectedModel] = useState(DEFAULT_MODEL)
  useEffect(() => {
    request<{ modelId: string | null }>(`/api/workspaces/${workspaceId}/preferences`)
      .then(data => { if (data.modelId) setSelectedModel(data.modelId) })
      .catch(() => {})
  }, [workspaceId])
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { workspaceId: string; modelId: string }
      if (detail.workspaceId === workspaceId && detail.modelId) setSelectedModel(detail.modelId)
    }
    window.addEventListener('papan:workspace-preference', handler)
    return () => window.removeEventListener('papan:workspace-preference', handler)
  }, [workspaceId])
  const handleModelSelect = useCallback(async (modelId: string) => {
    setSelectedModel(modelId)
    try {
      await request(`/api/workspaces/${workspaceId}/preferences`, {
        method: 'PUT',
        body: JSON.stringify({ modelId }),
      })
    } catch { /* best effort */ }
  }, [workspaceId])

  const { reasoningVariant, selectReasoningVariant } = useReasoningVariant(selectedModel)

  const handleComplete = useCallback(() => {
    void refreshSessions()
    onTurnComplete?.()
  }, [refreshSessions, onTurnComplete])

  const {
    messages,
    streamingText,
    streamingReasoning,
    activeToolCalls,
    status,
    pendingPermissions,
    pendingQuestions,
    expiredQuestionNotice,
    error,
    errorAction,
    sendMessage,
    steerMessage,
    skipOpenPrompts,
    cancel,
    dismissError,
    replyPermission,
    replyQuestion,
    rejectQuestion,
    restoreConversation,
    forkSession,
    summarize,
    summarizing,
    compacting,
    retry,
    turnActivity,
    moveAttachmentPart,
    verifyMovedAttachmentByPath,
  } = useAgentSession(sessionId, { workspaceId, onComplete: handleComplete, onOpenFile })

  const toast = useToast()
  // Auto mode (useConversationAutoMode.ts — shared with ChatPanel); the one draft here has no id, key ''
  const autoMode = useConversationAutoMode({
    chatId: sessionId ?? lastCreatedRef.current, draftKey: '',
    sessions, setSessionAutoMode, removeAlwaysGrant, fetchSession, listLoaded: sessionsLoaded, autoModeDefault, setAutoModeDefault, reloadAutoModeDefault, toast,
  })

  // #662 slice 9 — lazy clear seam for a SENT (transcript) chip: delegate the
  // open (and the host's "File not found" toast) to `onOpenFile` unchanged, and
  // additionally clear a moved chip's stale "In the drive" tick if its drive
  // file was deleted while this chat was closed. Only wired when the host gave
  // an opener, so a chip stays non-clickable when it has nothing to open.
  const handleTranscriptOpenFile = useCallback((path: string, at?: { line?: number }) => {
    onOpenFile?.(path, at)
    void verifyMovedAttachmentByPath(path)
  }, [onOpenFile, verifyMovedAttachmentByPath])
  // Tenant state, read once by the shell's provider — the same value the drive's
  // banner uses, so the two surfaces cannot disagree while a freeze is lifted.
  const { uploadsPaused } = useWriteStatus()
  // 0179: same rule as ChatPanel — hidden when the deployment's keybook is
  // off, unless this session already holds credentials that keep working.
  const keybookAvailable = useKeybookAvailable()

  /** The member's own chat-storage figure, for the banner above the composer
   *  (#758). The hero is a session's FIRST message, so there is no transcript
   *  growing underneath it to key a refetch on — a read on mount is the whole
   *  story, and `sessionId` re-reads it if the host swaps the hero's session
   *  without remounting. */
  const [chatQuota, setChatQuota] = useState<{ totalBytes: number; limitBytes: number | null } | null>(null)
  useEffect(() => {
    let alive = true
    request<{ member: { totalBytes: number; limitBytes: number | null } | null }>('/api/agent/sessions/chat-storage')
      .then((r) => { if (alive) setChatQuota(r.member ?? null) })
      // Silent, as in ChatPanel: the banner is a nudge, and an error toast for
      // a figure the member never asked for would be worse than no banner.
      .catch(() => {})
    return () => { alive = false }
  }, [sessionId])

  const isProcessing = isTurnLive(status)
  const hasUploading = attachments.some(a => a.status === 'uploading')

  // ── Session credentials (keybook) — same entryId-binding model as
  //    ChatPanel; values are resolved server-side at job submit time. ──
  const sessionCreds = useSessionCredentials({ onSyncRefused: (m) => toast.error(`Credential not added: ${m}`), onDropped: (m) => toast.warning(m, 10_000), activeToolCalls })
  const [showSessionKeybook, setShowSessionKeybook] = useState(false)
  const [showAddCredDialog, setShowAddCredDialog] = useState(false)
  const [pendingCredToFill, setPendingCredToFill] = useState<{ file?: string; label?: string; envMapping: Record<string, string>; requestId: string; isEdit?: boolean; currentValues?: Record<string, string>; currentVersion?: number; entryId?: string; provider?: string } | null>(null)
  const keybookBtnRef = useRef<HTMLButtonElement>(null)
  /** System messages buffered while no real session exists yet. */
  /** Single-flight for handleSend until the composer clears (send-gate.ts). */
  const sendGateRef = useRef(createSendGate())
  /** One in-flight session create (draft-materialize.ts). */
  const draftMaterializerRef = useRef(createDraftMaterializer())
  useEffect(() => { lastCreatedRef.current = null }, [sessionId])
  /** Bumped when a send clears the composer; a handleSend from an older render
   *  still holds the sent text and must not send it again. */
  const sendClearSeqRef = useRef(0)
  const renderClearSeq = sendClearSeqRef.current

  // ── Per-message actions (restore / branch / summarize + edit) ──
  //    Same state ChatPanel keeps; the rail owns session switching so `branch`
  //    hands the new session up via onSessionCreated instead of opening a tab.
  // The restore modal's message AND mode are ONE value, set together — so an
  // opening can never inherit an earlier opening's mode. As two states, "Restore
  // versions & send" once ran in a stale 'after' mode and RE-APPLIED the AI
  // changes it was meant to undo (review 2026-09-23).
  const [restoreTarget, setRestoreTarget] = useState<{ messageId: string; mode: 'before' | 'after' } | null>(null)
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null)
  const [editSendDialog, setEditSendDialog] = useState<{ messageId: string; text: string } | null>(null)
  const pendingEditRef = useRef<{ messageId: string; text: string } | null>(null)

  useEffect(() => {
    if (sessionId && workspaceId) {
      sessionCreds.load(sessionId, workspaceId)
    } else {
      sessionCreds.clear()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, workspaceId])


  // ── Attachments ──────────────────────────────────────────────────────
  // #1292: the per-message inline budget, upload cancel and send guard — shared
  // with ChatPanel (composerAttachments.ts), one composer list per chat.
  const attachmentControls = useComposerAttachmentControls({ lists: attachmentsMap, activeKey: heroAttachmentKey, workspaceId, toast })
  const { reserveInline, pickFor, keptForSend, refuseUnsendable: refuseUnsendableAttachments } = attachmentControls
  // By chip id, in whichever chat holds it (`updateListItemById`): an upload
  // finishes after the user may have switched chats.
  const handleRemoveAttachment = useCallback((id: string) => {
    attachmentControls.cancel(id)
    setAttachmentsMap(prev => updateListItemById(prev, id, () => null))
  }, [attachmentControls.cancel])

  const attachCallbacks = {
    onAdd: (att: Attachment) => setAttachments(prev => [...prev, att]),
    onUpdateProgress: (id: string, progress: number) => setAttachmentsMap(prev => updateListItemById(prev, id, a => ({ ...a, progress }))),
    onReady: (id: string, updates: Partial<Attachment>) => {
      attachmentControls.settled(id, false, updates.handle)
      setAttachmentsMap(prev => updateListItemById(prev, id, a => ({ ...a, ...updates, status: 'ready' as const })))
    },
    onError: (id: string) => {
      attachmentControls.settled(id, true)
      setAttachmentsMap(prev => updateListItemById(prev, id, a => ({ ...a, status: 'error' as const })))
    },
    onBlocked: (filename: string, reason: string) => toast.error(`${filename}: ${reason}`),
  }

  // #662 Task 8 — a COMPOSER (pre-send) chip was just moved to the drive.
  // Mirrors ChatPanel's handler: no persisted message part exists yet to
  // re-point server-side, so patch the matching composer attachment locally
  // (markAttachmentMoved) so the eventual send carries fileId+path.
  const handleComposerAttachmentMoved = useCallback((id: string, drive: { fileId: string; path: string }) => {
    setAttachmentsMap(prev => updateListItemById(prev, id, a => markAttachmentMoved(a, drive)))
  }, [])

  /** Materialize the session if needed (uploads land in the session-private
   *  CAS store — #639, Task 3.4). */
  const ensureSession = useCallback(async (): Promise<string | null> => {
    if (sessionId) return sessionId
    if (lastCreatedRef.current) return lastCreatedRef.current
    // One create even when a paste races the first send (draft-materialize.ts).
    // HeroChat's draft has no id, so this is de-duplicated only while in flight;
    // both racers get the same sid, and only the create reports it upward.
    return draftMaterializerRef.current.materialize(null, async () => {
      const sid = await createSession(undefined, selectedModel, autoMode.draftValue(''))
      if (sid) { lastCreatedRef.current = sid; autoMode.clearDraft(''); onSessionCreated?.(sid) }
      return sid
    })
  }, [sessionId, createSession, selectedModel, onSessionCreated, autoMode.draftValue, autoMode.clearDraft])

  const processToSessionStore = useCallback(async (files: File[], source: 'paste' | 'drop' | 'attach') => {
    const sid = await ensureSession()
    if (!sid) return
    // Every chip of this pick belongs to the chat it uploads into, whichever chat
    // is on screen when each is added (they are added one by one, after each
    // previous upload).
    // #1292 review round 6: the pick can outlive this composer (a project switch
    // remounts it) — a stale pick's later files are neither added nor uploaded.
    const pick = pickFor(sid)
    void processFiles(files, source, {
      ...attachCallbacks,
      onAdd: (att: Attachment) => { if (pick.alive()) setAttachmentsMap(prev => setKeyedList(prev, sid, list => [...list, att])) },
    }, {
      sessionId: sid, workspaceId, canWrite,
      reserveInline: (id, bytes) => reserveInline(id, bytes, sid),
      uploadSignal: pick.signal,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ensureSession, workspaceId, canWrite, reserveInline, pickFor])

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const files = Array.from(e.clipboardData.files)
    if (files.length === 0) return
    e.preventDefault()
    void processToSessionStore(files, 'paste')
  }, [processToSessionStore])

  // ── Send / steer ─────────────────────────────────────────────────────
  const handleSend = useCallback(async () => {
    const text = message.trim()
    const readyAttachments = attachments.filter(a => a.status === 'ready')
    if ((!text && readyAttachments.length === 0) || hasUploading) return
    // A press handled before React applied the last send's clear: this closure
    // still holds the text that was just sent.
    if (sendClearSeqRef.current !== renderClearSeq) return

    // One Enter sends ONCE: held until the composer clears (see send-gate.ts),
    // released before the turn is awaited so a mid-turn Enter still steers.
    const release = sendGateRef.current.tryEnter()
    if (!release) return
    let endSend = () => {}
    try {
      // Mid-run: steer — inject into the running turn at the next boundary.
      let historyAppended = false // set when a steer attempt already recorded it
      // E7 Codex round 10: the text comes back ONLY into the session it was sent from — this surface has one composer,
      // so after a switch to another session nothing is restored there (the refusal is still told)
      const sentFrom = sessionId
      if (isProcessing) {
        if (!sessionId || !text) return
        setMessage('')
        sendClearSeqRef.current++
        release()
        history.append(text); history.resetNavigation(); historyAppended = true
        // #648: skip an open prompt, else steer — the one rule for every chat surface (send-mid-run.ts)
        const outcome = await sendMidRun({
          text,
          hasOpenCards: pendingPermissions.length > 0 || pendingQuestions.length > 0,
          skip: skipOpenPrompts,
          steer: steerMessage,
          describeRefusal: describeSteerRefusal,
          stillFinishingNotice: i18n.t('agent:steer.stillFinishing'),
        })
        if (outcome.kind === 'steered') return
        if (outcome.kind === 'restore') {
          if (heroSessionRef.current === sentFrom) setMessage(prev => prev || text)
          if (outcome.notice) toast.info(outcome.notice)
          return
        }
        // 'send': no run to steer into — send it as an ordinary prompt right now
      }

      // #1292 (owner): a failed or uploading chip never lets the message go
      // without it — after the steer branch (it sends text only); a steer that
      // fell through already cleared the composer, so the text comes back.
      if (refuseUnsendableAttachments(attachments)) {
        if (historyAppended && heroSessionRef.current === sentFrom) setMessage(prev => prev || text)
        return
      }

      // #1292 D7: from here until the send ends, removing one of these chips holds
      // its server delete (the send may be promoting the row) — composerAttachments.
      endSend = attachmentControls.beginSend(readyAttachments.map(a => a.id))

      const sid = await ensureSession()
      if (!sid) return

      if (text && !historyAppended) {
        history.append(text)
        history.resetNavigation()
      }

      // A draft's credential rows (and the notes of keybook-added ones) are saved to the new session BEFORE the first
      // message (round 11 — one await, the hook's flush); a no-op for a session that was never a draft
      await sessionCreds.flushDraft(sid, workspaceId)

      // Clear optimistically — sendMessage awaits the whole turn stream. A mid-run send already cleared it before its
      // skip / steer (#648: the skip can wait up to a minute) — clearing again could wipe a draft typed since.
      if (!historyAppended) setMessage('')
      // Only what this send took: a chip pasted while the session was being
      // created is still uploading into this composer.
      setAttachmentsMap(prev => setKeyedList(prev, sid, list => withoutSent(list, readyAttachments)))
      sendClearSeqRef.current++
      release()
      // #1292 D7 (review round 2): a chip the user removed while this send was
      // getting ready is NOT sent — removing is "don't send this". Read now, after
      // the last await, outside any state updater (React may run one later, after
      // the finally has cleared the flag).
      const sending = keptForSend(readyAttachments) ?? []
      if (!text && sending.length === 0) return
      const success = await sendMessage(
        text, sending.length > 0 ? sending : undefined,
        selectedModel, sid, undefined, contextTab, contextSelection,
        getClientId(), undefined, reasoningVariant,
      )
      if (!success) {
        // E7 Codex round 10's rule (as the steer path above): this surface has ONE
        // text box, so the text comes back only while the chat it was sent from is
        // still the one on screen (review round 4) — it stays in ↑ history either way.
        if (heroAttachmentKey() === sid) setMessage(prev => prev || text)
        // Asked again now, outside any updater and before the finally's endSend():
        // a chip removed during the turn must not come back with a deleted handle.
        const back = keptForSend(sending) ?? []
        // Into the chat it was sent from, whichever is on screen by now.
        setAttachmentsMap(prev => restoreListByKey(prev, sid, back))
      }
    } finally {
      release()
      endSend()
    }
  }, [message, attachments, hasUploading, renderClearSeq, isProcessing, sessionId, steerMessage, ensureSession, history, sendMessage, selectedModel, reasoningVariant, workspaceId, contextTab, contextSelection, refuseUnsendableAttachments, attachmentControls.beginSend, keptForSend, skipOpenPrompts, pendingPermissions.length, pendingQuestions.length])

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      // An IME commit is not a send; let the IME have the key.
      if (e.nativeEvent.isComposing || e.nativeEvent.keyCode === 229) return
      e.preventDefault()
      // A held Enter auto-repeats (macOS: from 150 ms) — one press, one send.
      if (e.repeat) return
      void handleSend()
      return
    }
    // Arrow history — same affordance as the workspace composer.
    if (e.key === 'ArrowUp' && !e.shiftKey && !message) {
      const result = history.navigateUp(message)
      if (result !== null) { e.preventDefault(); setMessage(result) }
    }
    if (e.key === 'ArrowDown' && !e.shiftKey && history.index >= 0) {
      e.preventDefault()
      const result = history.navigateDown()
      if (result !== null) setMessage(result)
    }
  }, [handleSend, history, message])

  // Auto-resize when message changes programmatically (history recall).
  useEffect(() => {
    const ta = inputRef.current
    if (ta) {
      ta.style.height = 'auto'
      ta.style.height = `${Math.min(ta.scrollHeight, 160)}px`
    }
  }, [message])

  // Welcome starters: prefill the composer (caret at end) and optionally pop
  // the attach picker. The picker click must stay synchronous with the user
  // gesture, so it fires before the rAF that waits for the re-render.
  const startFromWelcome = useCallback((prompt: string, attach?: boolean) => {
    setMessage(prompt)
    if (attach) fileInputRef.current?.click()
    requestAnimationFrame(() => {
      const ta = inputRef.current
      if (!ta) return
      ta.focus()
      ta.setSelectionRange(ta.value.length, ta.value.length)
    })
  }, [])

  // ── Message actions (restore-versions / branch / summarize) ──
  //    Verbatim from ChatPanel.handleMessageAction; `branch` is adapted to the
  //    rail-controlled model (fork → refresh → onSessionCreated) since chat
  //    home has no tab strip to open the fork into.
  const handleMessageAction = useCallback(async (action: MessageAction, messageId: string) => {
    switch (action) {
      case 'restore-versions': {
        setRestoreTarget({ messageId, mode: 'before' })
        break
      }
      case 'restore-versions-after': {
        setRestoreTarget({ messageId, mode: 'after' })
        break
      }
      case 'summarize': {
        const t = describeSummarize(await summarize())
        toast[t.kind](t.text)
        break
      }
      case 'branch': {
        const result = await forkSession(messageId)
        if (result) {
          await refreshSessions()
          // Hand the fork up to the rail (no tab strip on chat home). The
          // branch-point message was copied with a new id — auto-open it in
          // edit mode; editingMessageId is surface-local so it survives the
          // session switch the host performs on onSessionCreated.
          if (result.branchedMessageID) setEditingMessageId(result.branchedMessageID)
          onSessionCreated?.(result.id)
        }
        break
      }
    }
  }, [summarize, forkSession, refreshSessions, onSessionCreated, toast])

  // ── Edit & re-send (ChatPanel.handleEditSend + choice/restore flows) ──
  const handleEditSend = useCallback(async (messageId: string, editedText: string) => {
    // Pre-check: abort if session is busy (avoids deleting messages then failing to send)
    if (status === 'processing' || status === 'submitting') {
      toast.error('Session is busy — please wait or dismiss the error first')
      return
    }
    // Check if there are file changes from this message onwards (not session-wide)
    const msgIdx = messages.findIndex(m => m.id === messageId)
    const hasFileChanges = msgIdx >= 0 && messages.slice(msgIdx).some(m =>
      m.parts?.some(p => p.type === 'patch' && (p.snapshots?.length ?? 0) > 0))
    // #1292: before the dialog and before the truncation — a refused resend must
    // not have deleted the original message first.
    if (refuseUnsendableAttachments(attachments)) return
    if (hasFileChanges) {
      setEditSendDialog({ messageId, text: editedText })
      return
    }
    const editAttachments = attachments.length > 0 ? attachments : undefined
    // The chat being edited: the resend's turn can outlast a switch to another chat.
    const editKey = heroAttachmentKey()
    // #639 Task 3.8: handles of attachments this edit is KEEPING (about to be
    // resent) — excluded from the restore's synchronous dereference, or the
    // resend below would reference a row the truncation just deleted.
    // Asked when the truncation STARTS (keptForSend): a chip removed before then is
    // neither kept nor sent. One removed DURING the truncation is kept but not sent —
    // a hydrated (already attached) one is then reaped by the attached-row sweep.
    const keepAttachmentHandles = () => keptForSend(editAttachments)?.map(a => a.handle).filter((h): h is string => !!h)
    const endSend = attachmentControls.beginSend((editAttachments ?? []).map(a => a.id))
    try { await truncateAndResend({
      restore: () => restoreConversation(messageId, { inclusive: true, keepAttachmentHandles: keepAttachmentHandles() }),
      // A chip removed during the truncation is not sent (read when the send starts).
      send: () => sendMessage(editedText, keptForSend(editAttachments), selectedModel, undefined, undefined, contextTab, contextSelection, getClientId(), undefined, reasoningVariant),
      onSent: () => { if (editAttachments) setAttachmentsMap(p => setKeyedList(p, editKey, list => withoutSent(list, editAttachments))); setEditingMessageId(null) },
      // R1.2: the truncation already removed the original, but the send didn't land
      // (session busy after the 409 retries). Keep the edited text in the composer so
      // it isn't lost; drop edit mode. Attachments are retained for the retry.
      onFailed: () => {
        setMessage(prev => mergeDraft(prev, editedText))
        setEditingMessageId(null)
        toast.error('Session busy — your edit was kept in the composer. Press Send to retry.')
      },
    }) } finally { endSend() }
  }, [status, messages, restoreConversation, sendMessage, selectedModel, reasoningVariant, toast, attachments, contextTab, contextSelection, refuseUnsendableAttachments, attachmentControls.beginSend, keptForSend])

  const handleEditSendChoice = useCallback(async (choice: 'restore' | 'keep') => {
    const pending = editSendDialog
    if (!pending) return
    setEditSendDialog(null)

    // #1292: the chips may have changed while the dialog was open — checked
    // before EITHER choice, so a refused resend restores no files either.
    if (refuseUnsendableAttachments(attachments)) return

    if (choice === 'restore') {
      // Open restore modal — store pending edit for after restore completes
      pendingEditRef.current = pending
      setRestoreTarget({ messageId: pending.messageId, mode: 'before' })
      return
    }

    const editAttachments = attachments.length > 0 ? attachments : undefined
    // The chat being edited: the resend's turn can outlast a switch to another chat.
    const editKey = heroAttachmentKey()
    // Asked when the truncation STARTS (keptForSend): a chip removed before then is
    // neither kept nor sent. One removed DURING the truncation is kept but not sent —
    // a hydrated (already attached) one is then reaped by the attached-row sweep.
    const keepAttachmentHandles = () => keptForSend(editAttachments)?.map(a => a.handle).filter((h): h is string => !!h)
    const endSend = attachmentControls.beginSend((editAttachments ?? []).map(a => a.id))
    try { await truncateAndResend({
      restore: () => restoreConversation(pending.messageId, { inclusive: true, keepAttachmentHandles: keepAttachmentHandles() }),
      send: () => sendMessage(pending.text, keptForSend(editAttachments), selectedModel, undefined, undefined, contextTab, contextSelection, getClientId(), undefined, reasoningVariant),
      onSent: () => { if (editAttachments) setAttachmentsMap(p => setKeyedList(p, editKey, list => withoutSent(list, editAttachments))); setEditingMessageId(null) },
      onFailed: () => { // R1.2: retain the edited draft on a failed send after truncation
        setMessage(prev => mergeDraft(prev, pending.text))
        setEditingMessageId(null)
        toast.error('Session busy — your edit was kept in the composer. Press Send to retry.')
      },
    }) } finally { endSend() }
  }, [editSendDialog, restoreConversation, sendMessage, selectedModel, reasoningVariant, toast, attachments, contextTab, contextSelection, refuseUnsendableAttachments, attachmentControls.beginSend, keptForSend])

  // Called when restore modal completes from a pending edit
  const handleRestoreAndSendComplete = useCallback(async (restored: RestoredFile[]) => {
    const pending = pendingEditRef.current
    if (!pending) return
    pendingEditRef.current = null
    setRestoreTarget(null)
    // #1292: a chip failed while the restore ran. The files ARE restored; the
    // resend (and its truncation) must not run. Stay in edit mode so the user
    // fixes the chip and sends the edit again.
    // The modal calls back with the callback it captured at the Restore click, so
    // read the LIVE list, not this closure's.
    const liveAttachments = attachmentControls.liveList()
    const unsendable = unsendableAttachmentNotice(liveAttachments)
    if (unsendable) {
      toast.error(restored.length > 0 ? editNotSentMessage(describeRestored(restored), unsendable.message) : unsendable.message)
      return
    }
    const editAttachments = liveAttachments.length > 0 ? liveAttachments : undefined
    const editKey = heroAttachmentKey()
    // Asked when the truncation STARTS (keptForSend): a chip removed before then is
    // neither kept nor sent. One removed DURING the truncation is kept but not sent —
    // a hydrated (already attached) one is then reaped by the attached-row sweep.
    const keepAttachmentHandles = () => keptForSend(editAttachments)?.map(a => a.handle).filter((h): h is string => !!h)
    const endSend = attachmentControls.beginSend((editAttachments ?? []).map(a => a.id))
    try { await truncateAndResend({
      restore: () => restoreConversation(pending.messageId, { inclusive: true, keepAttachmentHandles: keepAttachmentHandles() }),
      // Same send as the "keep" path — reasoning variant included, and the
      // edit's attachments cleared from the composer once sent (review
      // 2026-09-23: "Send without restoring" routes more resends through here).
      send: () => sendMessage(pending.text, keptForSend(editAttachments), selectedModel, undefined, undefined, contextTab, contextSelection, getClientId(), undefined, reasoningVariant),
      onSent: () => {
        if (editAttachments) setAttachmentsMap(p => setKeyedList(p, editKey, list => withoutSent(list, editAttachments)))
        setEditingMessageId(null)
        if (restored.length > 0) {
          toast.success(`${describeRestored(restored)} and resent`)
        }
      },
      // R1.2: retain the edited draft on a failed send after truncation (don't also
      // claim "restored and resent" — that success toast now only fires on success).
      onFailed: () => {
        setMessage(prev => mergeDraft(prev, pending.text))
        setEditingMessageId(null)
        toast.error('Session busy — your edit was kept in the composer. Press Send to retry.')
      },
    }) } finally { endSend() }
  }, [restoreConversation, sendMessage, selectedModel, toast, attachments, contextTab, contextSelection, reasoningVariant, attachmentControls.beginSend, keptForSend])

  // Stop is ALWAYS available while a run is in flight (parity with ChatPanel) —
  // typing steering text must not hide it. Send stays usable alongside so a
  // typed message mid-run steers the running turn (handleSend routes it).
  // ── THE SESSION'S WALLET (0180) ───────────────────────────────────────
  //
  // The SECOND chat surface. `ChatPanel` got this first and this one was
  // missed, which is the same shape as gating two of three send paths: the
  // refusal still happens server-side, it just arrives as a 409 toast instead
  // of a disabled composer, on the surface most people actually use.
  //
  // `=== false` because the field is ABSENT on an older API during a rolling
  // restart, and locking on absence would disable chat for everyone the moment
  // a deploy lagged.
  const activeSessionWallet = sessions.find(s => s.id === sessionId)?.wallet
  const walletLocked = activeSessionWallet?.canSend === false

  const canSend = (!!message.trim() || attachments.some(a => a.status === 'ready'))
    && !hasUploading && !walletLocked

  return (
    <div className="hero-chat h-full flex flex-col min-h-0">
      <MessageList
        summarizing={summarizing || compacting}
        sessionId={sessionId}
        messages={messages}
        streamingText={streamingText}
        streamingReasoning={streamingReasoning}
        pendingPermission={pendingPermissions[0] ?? null}
        onPermissionReply={replyPermission}
        pendingQuestions={pendingQuestions}
        expiredQuestionNotice={expiredQuestionNotice}
        onQuestionReply={replyQuestion}
        onQuestionDismiss={rejectQuestion}
        activeToolCalls={activeToolCalls}
        sessionCredentials={sessionCreds.credentials}
        onCredentialResolved={(envMapping, entryId, callId, fieldMap, opts) => {
          // THE card's own row, by its tool call id (round 11 — the one way to find it). RETURNED (I198 Codex
          // round): the card awaits the binding before it replies.
          if (!callId) return false
          return sessionCreds.resolveCard(callId, envMapping, entryId, fieldMap, opts)
        }}
        isProcessing={isProcessing}
        retry={retry}
        turnActivity={turnActivity}
        emptyState={<HeroWelcome onStart={startFromWelcome} onAttach={() => fileInputRef.current?.click()} />}
        showReasoning={displayOptions.showReasoning}
        showToolDetails={displayOptions.showToolDetails}
        showModelInfo={displayOptions.showModelInfo}
        showCost={costVisibility === 'user'}
        showTimestamps={displayOptions.showTimestamps}
        wrapLines={displayOptions.wrapDiffLines}
        onOpenFile={onOpenFile ? handleTranscriptOpenFile : undefined}
        onMoved={moveAttachmentPart}
        workspaceId={workspaceId}
        onMessageAction={handleMessageAction}
        editingMessageId={editingMessageId}
        onEditStart={isProcessing ? undefined : (id) => {
          // Hydrate the attachment area with the edited message's existing
          // file parts so they survive the re-send (verbatim from ChatPanel —
          // without this, editing silently drops every original attachment).
          const msg = messages.find(m => m.id === id)
          if (msg) {
            const hydrated: Attachment[] = msg.parts
              .filter((p): p is Extract<typeof p, { type: 'file' }> => p.type === 'file')
              .map(fp => ({
                id: `edit_${fp.id}`,
                filename: fp.filename,
                mime: fp.mime,
                url: fp.url,
                path: fp.path,
                source: (fp.source as Attachment['source']) || 'attach',
                status: 'ready' as const,
                // #639 Task 3.8: carry the handle through (verbatim from
                // ChatPanel) so a KEPT session-private binary survives the
                // resend instead of arriving handle-less.
                handle: fp.handle,
                // #662: carry the drive fileId too (verbatim from ChatPanel) —
                // otherwise editing+resending a moved attachment severs its
                // drive link. The outgoing send mapper reads `a.fileId`.
                fileId: fp.fileId,
                // #662: and the stashed inline bytes (revert-to-preview).
                previewUrl: fp.previewUrl,
              }))
            // #1292: the chips this replaces are dropped unsent — cancel their
            // uploads and delete what already landed (composerAttachments.ts).
            attachmentControls.discard(attachments.map(a => a.id))
            setAttachments(hydrated)
          }
          setEditingMessageId(id)
        }}
        onEditCancel={() => {
          // Drop the hydrated attachments so they don't bleed into the main
          // composer when the user returns to composing fresh — and, #1292, any
          // chip added during the edit: dropped unsent.
          attachmentControls.discard(attachments.map(a => a.id))
          setAttachments([])
          setEditingMessageId(null)
        }}
        onEditSend={handleEditSend}
        renderEditControls={editingMessageId ? (editText, onCancel, onSend) => (
          <>
            <AttachmentArea
              attachments={attachments}
              onRemove={handleRemoveAttachment}
              onOpen={onOpenFile}
              disabled={false}
              sessionId={sessionId}
              workspaceId={workspaceId}
              onMoved={handleComposerAttachmentMoved}
            />
            {/* Same layout as ChatPanel's edit row — see the note there. */}
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1.5">
              <div className="flex items-center gap-2 flex-1 min-w-min">
                <ModelSelector
                  selectedModel={selectedModel}
                  onSelect={handleModelSelect}
                  disabled={false}
                />
                {activeTab && (
                  <EditorContextChip
                    activeTab={activeTab}
                    selection={selection ?? null}
                    active={contextActive}
                    onToggle={() => setContextActive(v => !v)}
                    unsaved={unsaved}
                  />
                )}
              </div>
              <EditMessageButtons
                onAttach={() => fileInputRef.current?.click()}
                onSend={onSend}
                onCancel={onCancel}
                sendDisabled={!editText.trim()}
              />
            </div>
          </>
        ) : undefined}
      />

      {error && (
        <div className="mx-1 mb-2 px-3 py-2 rounded-xl bg-red-500/10 border border-red-500/20 text-xs text-red-400 flex items-start justify-between gap-2">
          <span className="flex-1 min-w-0 break-words whitespace-pre-wrap">
            {typeof error === 'string' ? error : String((error as { message?: unknown })?.message ?? error)}
          </span>
          <div className="flex gap-1.5 shrink-0">
            {/* Same contract as ChatPanel's banner: the gate says this reader
                can act, the host says it can offer the destination. Chat home
                is where a brand-new org sends its first message, so this is a
                likely place to run out — the two banners must not diverge. */}
            {errorAction && onOpenBilling && (
              <button
                onClick={() => { onOpenBilling(); dismissError() }}
                className="px-2 py-0.5 rounded bg-red-500/30 hover:bg-red-500/40 text-red-200 font-medium transition-colors"
              >
                {errorAction.label}
              </button>
            )}
            {/* DISMISS ONLY — deliberately not `cancel()`.
              *
              * This is the banner's only control, and it says Dismiss. It used
              * to call `cancel()` first, which posts /abort with no runId (this
              * surface never holds a live-run id), so `cancelSession` matched
              * SESSION-WIDE and killed every job on the session. While `cancel`
              * refused outright for an advisory that was merely inert here; once
              * it was correctly re-armed for a live turn, this button became a
              * "Dismiss" that aborts a turn started in another tab.
              *
              * Nothing is lost: `dismissError` force-unlocks a genuinely stuck
              * session by itself, which is the recovery this button is for.
              * ChatPanel keeps a separate, honestly-labelled Abort. */}
            <button
              onClick={() => dismissError()}
              className="px-2 py-0.5 rounded bg-red-500/20 hover:bg-red-500/30 text-red-300 transition-colors"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Pending credential form — inline above the composer. */}
      {pendingCredToFill && (
        <div className="mx-1 mb-2">
          <CredentialPrompt
            request={{ id: pendingCredToFill.requestId, sessionID: '', questions: [] }}
            file={pendingCredToFill.file}
            label={pendingCredToFill.label}
            envMapping={pendingCredToFill.envMapping}
            isEdit={pendingCredToFill.isEdit}
            initialValues={pendingCredToFill.currentValues}
            initialVersion={pendingCredToFill.currentVersion}
            initialEntryId={pendingCredToFill.entryId}
            provider={pendingCredToFill.provider}
            fields={sessionDialogFields(pendingCredToFill.envMapping)}
            onReply={() => setPendingCredToFill(null)}
            onDismiss={() => setPendingCredToFill(null)}
            onDelete={pendingCredToFill.isEdit ? () => {
              sessionCreds.remove(pendingCredToFill.requestId)
              setPendingCredToFill(null)
            } : undefined}
            onCredentialResolved={async (entryId, _saved, credential) => {
              // await bind() before sendMessage — the message route's
              // resolver reads the session_credential row (see ChatPanel
              // for the race this closes).
              if (entryId) {
                await sessionCreds.bind(pendingCredToFill.requestId, entryId, undefined, credential?.fieldMap)   // #1441: the card's exact field map
              } else {
                sessionCreds.remove(pendingCredToFill.requestId, 'user')
              }
              if (pendingCredToFill.isEdit) {
                setPendingCredToFill(null)
              } else {
                const fileName = pendingCredToFill.file?.split('/').pop() || pendingCredToFill.label || 'Credentials'
                const envVars = Object.entries(pendingCredToFill.envMapping).map(([, v]) => `$${v}`).join(', ')
                void sendMessage(
                  `${fileName} credentials ready`,
                  undefined, undefined, undefined, undefined, undefined, undefined, undefined,
                  `Credentials submitted by user. Available environment variables: ${envVars}. Use them directly in bash — do not call cred_to_env again.`,
                )
                setPendingCredToFill(null)
              }
            }}
          />
        </div>
      )}

      {/* Decision 9, honoured at the only moment that helps: BEFORE the user
          presses send.

          An agent turn burns credit and *then* writes its files, so a frozen
          tenant's turn is charged and its creates are refused at end-of-turn sync
          — the customer pays for work that is discarded. The plan proposed warning
          on the enqueue RESPONSE, but the chat's enqueue response IS the AG-UI SSE
          stream, so there is no field to carry it; and a warning after the turn is
          accepted is already too late. Reading tenant state instead puts the notice
          on screen while the composer is still empty, which is strictly earlier
          than the plan asked for.

          A notice, NOT a disabled composer: the freeze blocks new FILES, not
          conversation, and plenty of useful turns edit existing files or write
          nothing at all. Refusing to let a paused customer talk to the agent would
          take away something they have not lost. */}
      {/* Shared with ChatPanel, which had no notice at all until this was
          extracted — the hero is only a session's FIRST message, so the surface
          that lost the explanation was the one people spend the most time in.
          Localized too: the note this replaced claimed the file had no
          `useTranslation` and that adding one "would read as an accident", but
          ModelSelector and SessionListDialog in this same package both use it. */}
      {uploadsPaused && <UploadsPausedNotice className="mx-4 mb-1.5" />}

      {/* #758: the chat-storage warning. Same component as ChatPanel so the
          two surfaces cannot word it differently — `standalone` because the
          hero composer has no border for it to sit flush on. */}
      <ChatQuotaBanner
        usedBytes={chatQuota?.totalBytes ?? 0}
        limitBytes={chatQuota?.limitBytes ?? null}
        onManage={onManageChatStorage}
        shape="standalone"
      />

      {/* Read-only because this conversation belongs to a different wallet
          (0180) — sits beside the uploads-paused notice for the same reason it
          exists: a composer that refuses input without saying why reads as a
          bug. Unlike that one this DOES disable the composer, because the
          refusal here is real and server-enforced rather than advisory. */}
      {walletLocked && (
        <div className="mx-4 mb-1.5 flex items-start gap-2 px-3 py-2 rounded-lg bg-sky-500/10 border border-sky-500/20">
          <span className="text-xs themed-text-muted flex-1 leading-snug">
            {activeSessionWallet?.reason?.message
              ?? 'This conversation belongs to a different wallet and is read-only here.'}
          </span>
          <button
            type="button"
            onClick={async () => {
              const result = await forkSession()
              if (!result) { toast.error('Could not fork this conversation'); return }
              await refreshSessions()
              onSessionCreated?.(result.id)
            }}
            className="text-xs px-2 py-1 rounded themed-btn-secondary shrink-0"
          >
            Fork to continue
          </button>
        </div>
      )}

      {/* Composer — one soft wash, no card chrome. */}
      <div className="chat-hero-composer shrink-0 px-4 pt-3 pb-2.5 mx-1 mb-1">
        <AttachmentArea
          attachments={attachments}
          onRemove={handleRemoveAttachment}
          onOpen={onOpenFile}
          disabled={false}
          sessionId={sessionId}
          workspaceId={workspaceId}
          onMoved={handleComposerAttachmentMoved}
        />
        <textarea
          ref={inputRef}
          value={message}
          onChange={e => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder={walletLocked ? 'Read-only — fork to continue' : isProcessing ? 'Add instructions while it works…' : 'Ask anything…'}
          disabled={walletLocked}
          rows={1}
          className="w-full bg-transparent themed-text outline-none placeholder:themed-text-muted resize-none max-h-[160px] min-h-[24px] text-[15px] leading-relaxed"
          style={{ height: 'auto' }}
          onInput={e => {
            const target = e.target as HTMLTextAreaElement
            target.style.height = 'auto'
            target.style.height = `${Math.min(target.scrollHeight, 160)}px`
          }}
        />
        {/* Same wrap rule as ChatPanel's composer row — see the note there. */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1.5">
          <div className="flex items-center gap-2 flex-1 min-w-min">
            <ModelSelector
              selectedModel={selectedModel}
              onSelect={handleModelSelect}
              variant={reasoningVariant}
              onVariantChange={selectReasoningVariant}
              disabled={isProcessing}
            />
            <AutoModeButton {...autoMode.chip} onOpen={autoMode.isDraft ? undefined : autoMode.isListed ? refreshSessions : autoMode.reread} />
            {activeTab && (
              <EditorContextChip
                activeTab={activeTab}
                selection={selection ?? null}
                active={contextActive}
                onToggle={() => setContextActive(v => !v)}
                unsaved={unsaved}
              />
            )}
          </div>
          <div className="flex items-center gap-2 ml-auto">
            {/* Attach — disabled with a reason for view-only members, same
                component as ChatPanel. */}
            <ComposerAttachButton canWrite={canWrite} onPick={() => fileInputRef.current?.click()} />
            {/* Session keybook — hidden when the deployment has the keybook off
                AND nothing is bound to this session (0179). */}
            {(keybookAvailable || sessionCreds.credentials.length > 0) && (
            <div className="relative shrink-0">
              <button
                ref={keybookBtnRef}
                onClick={() => {
                  if (pendingCredToFill) setPendingCredToFill(null)
                  else setShowSessionKeybook(v => !v)
                }}
                className={`${sessionCreds.credentials.length > 0 ? 'themed-text-secondary hover:themed-text' : 'themed-text-muted hover:themed-text'} transition-colors p-1 relative`}
                title="Session credentials"
                aria-label="Session credentials"
              >
                <BookKey size={15} />
                {hasOpenablePending(sessionCreds.credentials) && (
                  <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-yellow-400" />
                )}
              </button>
              {showSessionKeybook && (
                <SessionKeybookPopover
                  credentials={sessionCreds.credentials}
                  onClose={() => setShowSessionKeybook(false)}
                  anchorRef={keybookBtnRef}
                  onPendingClick={async (cred) => {
                    setShowSessionKeybook(false)
                    // Edit mode pre-fills current values from the keybook entry.
                    let currentValues: Record<string, string> | undefined
                    let currentVersion: number | undefined
                    if (cred.status === 'active' && cred.entryId) {
                      try {
                        const { keybookApi } = await import('@papan/keybook/client')
                        const detail = await keybookApi.getEntry(cred.entryId)
                        const data = (detail.entry.data ?? { fields: {} }) as { fields: Record<string, string> }
                        currentValues = data.fields
                        // #1324 I2: Update is sent against the version THESE values were read at
                        const v = (detail.entry as { version?: unknown }).version
                        currentVersion = typeof v === 'number' ? v : undefined
                      } catch { /* form opens blank */ }
                    }
                    setPendingCredToFill({
                      requestId: cred.requestId,
                      file: cred.file,
                      label: cred.label,
                      envMapping: sessionDialogEnvMapping(cred),
                      isEdit: cred.status === 'active',
                      currentValues,
                      currentVersion,
                      entryId: cred.entryId,
                      provider: cred.provider,
                    })
                  }}
                  onAddClick={() => {
                    setShowSessionKeybook(false)
                    setShowAddCredDialog(true)
                  }}
                />
              )}
              {showAddCredDialog && (
                <AddSessionCredentialDialog
                  existingEntryIds={new Set(sessionCreds.credentials.map(c => c.entryId).filter((id): id is string => !!id))}
                  onClose={() => setShowAddCredDialog(false)}
                  onAdd={(resolved) => {
                    // the hook tells the model once the row is saved (a draft: once flushDraft lands it)
                    sessionCreds.addFromKeybook({
                      entryId: resolved.entryId,
                      entryLabel: resolved.entryLabel,
                      file: resolved.file,
                      envMapping: resolved.envMapping,
                    })
                  }}
                />
              )}
            </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                const files = Array.from(e.target.files || [])
                if (files.length > 0) void processToSessionStore(files, 'attach')
                e.target.value = ''
              }}
            />
            {/* Stop — a SEPARATE affordance shown whenever a run is in flight,
                so a stuck run can be halted even while steering text is typed
                (BUG B fix; ChatPanel keeps Stop always-available too). */}
            {isProcessing && (
              <button
                onClick={() => { void cancel() }}
                className="w-8 h-8 rounded-full flex items-center justify-center bg-red-500/15 text-red-400 hover:bg-red-500/25 transition-colors shrink-0"
                title="Stop the agent"
                aria-label="Stop"
              >
                <StopCircle size={15} />
              </button>
            )}
            {/* Send / steer (Enter). Mid-run a non-empty send steers the turn. */}
            <button
              onClick={() => { void handleSend() }}
              disabled={!canSend}
              className="w-8 h-8 rounded-full flex items-center justify-center transition-colors themed-btn-primary text-white disabled:opacity-30 shrink-0"
              title={isProcessing ? 'Steer the agent (Enter)' : 'Send (Enter)'}
              aria-label={isProcessing ? 'Steer' : 'Send'}
            >
              <Send size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Message-action modals — parity with ChatPanel. Gated on `sessionId`
          (chat home's equivalent of ChatPanel's openTabs.activeId). */}
      {restoreTarget && sessionId && (
        <RestoreVersionsModal
          sessionId={sessionId}
          messageId={restoreTarget.messageId}
          workspaceId={workspaceId}
          onClose={() => {
            pendingEditRef.current = null
            setRestoreTarget(null)
          }}
          onRestored={pendingEditRef.current
            ? handleRestoreAndSendComplete
            : (restored) => {
                toast.success(describeRestored(restored))
              }
          }
          andSend={!!pendingEditRef.current}
          mode={restoreTarget.mode}
        />
      )}

      {editSendDialog && (
        <EditSendDialog
          onChoice={handleEditSendChoice}
          onCancel={() => setEditSendDialog(null)}
        />
      )}
    </div>
  )
}

/** One-tap example prompts under the welcome cards. `attach` chips need a
 *  file to act on, so they open the picker in the same gesture — cancelling
 *  it just leaves the prompt in the composer for the paperclip later. */
const STARTER_CHIPS: Array<{ label: string; prompt: string; attach?: boolean }> = [
  { label: 'Build an expense splitter', prompt: 'Build a PDApp that lets my team log shared expenses and see who owes who.' },
  { label: 'Spreadsheet to dashboard', prompt: 'Turn this spreadsheet into a live dashboard of its key numbers.', attach: true },
  { label: 'Sort my invoices', prompt: 'Sort these invoices into folders by month and give me a summary table.', attach: true },
  { label: 'Summarize PDFs', prompt: 'Summarize each of these PDFs in a few bullet points.', attach: true },
]

/** Chat-home welcome — the empty-state hero. Two "job" cards teach the
 *  product's two starts (build a live mini app / upload files) and the
 *  chips drop a ready-to-edit prompt into the composer; nothing auto-sends.
 *  Surfaces use the landing's soft-wash language (.chat-start-card in
 *  index.css), not the opaque themed-bg tokens — the hero floats bare on
 *  the stage. Wrapper: m-auto instead of items-center so a short viewport
 *  (mobile keyboard up) scrolls instead of clipping the top.
 *
 *  Responsive by CONTAINER, not viewport: the hero column is squeezed by
 *  the side panels (editor takeover can take 70% of the window), so sm:
 *  breakpoints lie here — auto-fit columns collapse from the width the
 *  grid actually has (min(220px,100%) also guards ultra-narrow overflow). */
function HeroWelcome({ onStart, onAttach }: {
  onStart: (prompt: string, attach?: boolean) => void
  onAttach: () => void
}) {
  return (
    <div className="flex-1 min-h-0 overflow-y-auto flex p-6">
      <div className="m-auto w-full max-w-[560px] text-center">
        <p className="text-lg themed-text mb-1">What should we make today?</p>
        <p className="text-xs themed-text-muted mb-5">Pick a starting point, or just ask anything.</p>
        <div className="grid gap-2.5 text-left grid-cols-[repeat(auto-fit,minmax(min(220px,100%),1fr))]">
          <button
            onClick={() => onStart('Build a PDApp that ')}
            className="chat-start-card rounded-2xl px-4 py-3.5 flex flex-col gap-1"
          >
            <span className="flex items-center gap-2">
              <PDAppIcon size={15} className="chat-app-icon shrink-0" aria-hidden />
              <span className="text-sm font-medium themed-text min-w-0">Build a live PDApp</span>
            </span>
            <span className="text-[11.5px] themed-text-muted leading-snug">
              Describe it in a sentence — a tracker, a form, a dashboard. Watch it go live.
            </span>
          </button>
          <button
            onClick={onAttach}
            className="chat-start-card rounded-2xl px-4 py-3.5 flex flex-col gap-1"
          >
            <span className="flex items-center gap-2">
              <FileUp size={15} className="text-sky-400 shrink-0" aria-hidden />
              <span className="text-sm font-medium themed-text min-w-0">Start with your files</span>
            </span>
            <span className="text-[11.5px] themed-text-muted leading-snug">
              Upload a spreadsheet or documents, then ask me to organize, analyze, or transform them.
            </span>
          </button>
        </div>
        <div className="flex flex-wrap justify-center gap-1.5 mt-4">
          {STARTER_CHIPS.map(chip => (
            <button
              key={chip.label}
              onClick={() => onStart(chip.prompt, chip.attach)}
              className="chat-start-card rounded-full px-3 py-1.5 text-xs themed-text-secondary hover:themed-text max-w-full truncate"
            >
              {chip.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
